/**
 * Closing the account: what goes, the full download first, and a typed confirmation.
 *
 * PRODUCT.md §8 promises the full download before an account's data is deleted. A full export
 * downloaded today counts; otherwise the owner ticks that they are closing without a copy. Typing
 * the account's email address is the last step, so the wrong tab or a stray tap cannot close it.
 * The server checks both again; this page only keeps the button off until they hold.
 */

import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ApiError } from "@/api/problem";
import { Button, Field, Input, Notice } from "@/components/ui/Field";
import { meQuery } from "@/features/auth/session";
import { SettingsHeader } from "@/features/settings/SettingsHeader";
import { sameEmail } from "@/features/settings/closing";
import { closingQuery, useCloseAccount } from "@/features/settings/queries";

const schema = z.object({ confirm_email: z.string(), skip_export: z.boolean() });
type Values = z.infer<typeof schema>;

export function CloseAccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const me = useQuery(meQuery);
  const closing = useQuery(closingQuery);
  const close = useCloseAccount();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { confirm_email: "", skip_export: false },
  });
  const typed = useWatch({ control: form.control, name: "confirm_email" });
  const skip = useWatch({ control: form.control, name: "skip_export" });

  const email = me.data?.email;
  const downloaded = closing.data?.export_downloaded ?? false;
  const ready = (downloaded || skip) && sameEmail(typed, email);

  const submit = form.handleSubmit(async (values) => {
    try {
      await close.mutateAsync({
        confirm_email: values.confirm_email,
        skip_export: !downloaded && values.skip_export,
      });
      await navigate({ to: "/auth/closed" });
    } catch (error) {
      form.setError("root", {
        message: error instanceof ApiError ? error.message : t("close.failed"),
      });
    }
  });

  if (closing.isPending) return <p className="text-stone-500">{t("common.loading")}</p>;
  if (closing.isError) return <Notice tone="error">{t("close.failed")}</Notice>;

  return (
    <section className="space-y-5">
      <SettingsHeader title={t("close.title")} description={t("close.subtitle")} />

      <div className="space-y-2 rounded-lg border border-stone-200 bg-white p-4 text-sm text-stone-700">
        <p className="font-medium text-stone-900">{t("close.whatHappens")}</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>{t("close.deleted")}</li>
          {closing.data.subscribed && <li>{t("close.billing")}</li>}
          <li>{t("close.invoices")}</li>
          <li>{t("close.paper")}</li>
        </ul>
      </div>

      <form onSubmit={submit} noValidate className="space-y-4">
        <div className="space-y-3 rounded-lg border border-stone-200 bg-white p-4">
          <p className="font-medium text-stone-900">{t("close.step1")}</p>
          {downloaded ? (
            <Notice tone="success">{t("close.downloaded")}</Notice>
          ) : (
            <>
              <p className="text-sm text-stone-700">
                {t("close.downloadFirst")}{" "}
                <Link to="/settings/data" className="text-brand-700 font-medium underline">
                  {t("data.title")}
                </Link>
              </p>
              <label className="flex items-start gap-3 text-sm text-stone-800">
                <input
                  type="checkbox"
                  className="mt-0.5 h-5 w-5 shrink-0"
                  {...form.register("skip_export")}
                />
                {t("close.skip")}
              </label>
            </>
          )}
        </div>

        <div className="space-y-3 rounded-lg border border-stone-200 bg-white p-4">
          <p className="font-medium text-stone-900">{t("close.step2")}</p>
          <Field name="confirm-email" label={t("close.typeEmail", { email: email ?? "" })}>
            <Input
              id="confirm-email"
              type="email"
              autoComplete="off"
              autoCapitalize="none"
              {...form.register("confirm_email")}
            />
          </Field>
        </div>

        {form.formState.errors.root && (
          <Notice tone="error">{form.formState.errors.root.message}</Notice>
        )}

        <Button
          type="submit"
          className="bg-red-700 hover:bg-red-800"
          disabled={!ready || close.isPending}
        >
          {close.isPending ? t("close.closing") : t("close.confirm")}
        </Button>
      </form>
    </section>
  );
}
